import React, { useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import mockedCropPlan from '../../mocks/mocks/crop-planning/cropPlan.json';

// constants
import { scriptTypes } from 'constants/scripts';

// action creators
import { setActiveCropId, setActiveCropPlan, setActiveScriptType } from 'current';
import { errorLevel } from 'constants/errors';
import { fetchBrandsByActiveCrop } from 'products';
import { startAdvancedFlow } from 'advanced';
import { startManualFlow } from 'manual';

// requesters
import { requestActivities } from 'activities/requesters';
import { requestAdvancedScriptAvailability } from 'advanced/requesters';

// selectors
import { getActiveScriptType } from 'current/selectors';
import { getActiveCropId } from 'current/currentSelectors';
import { currentCropCanAdvancedScript, getCropList, getSupportedCrops } from 'crops/selectors';
import { canAdvancedScript, canManualScript } from 'product-features/selectors';
import { isCropPlanIntegrationEnabled } from 'user/selectors';

// utils
import { buildUpgradeUrl } from 'util/urls';
import { treatErrorAs } from 'api/errors';
import { getSearchParamOnPluginURL } from 'util/queryString';
import { useCropPlanStash } from 'util/hooks/useCropPlanStash';

// components
import ScriptTypeToggleContainer from 'common/ScriptTypeToggleContainer';
import FertilityScriptOptions from './FertilityScriptOptions';
import PlantingScriptOptionsConnected from './PlantingScriptOptions';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { CROP_PLAN_INTEGRATION } from 'constants/cropPlanning';

const baseString = 'vra-script-options';
const screenClassString = classNames({
  [`${baseString}`]: true,
  'vra-create-script-screen': true
});

const ScriptOptions = () => {
  const activeCropId = useAppSelector(getActiveCropId).toString();
  const activeScriptType = useAppSelector(getActiveScriptType);
  const canCreateAdvancedScript = useAppSelector(canAdvancedScript);
  const canCreateManualScript = useAppSelector(canManualScript);
  const cropCanAdvancedScript = useAppSelector(currentCropCanAdvancedScript);
  const isCropPlanIntegration = useAppSelector(isCropPlanIntegrationEnabled);
  const supportedCrops = useAppSelector(getSupportedCrops);
  const upgradePath = useAppSelector(buildUpgradeUrl);
  const crops = useAppSelector(getCropList);
  const dispatch = useAppDispatch();
  // The query params are used for development and testing purposes only.
  const cropPlanParam = getSearchParamOnPluginURL(CROP_PLAN_INTEGRATION.queryParamKey);
  const [cropPlan, setCropPlan] = useCropPlanStash();

  const onClickAdvancedScriptTrigger = useCallback(() => {
    dispatch(startAdvancedFlow());
  }, [dispatch]);

  const onClickManualScriptTrigger = useCallback(() => {
    try {
      dispatch(startManualFlow());
    } catch (error) {
      treatErrorAs({
        severity: errorLevel.WARN,
        messageKey: 'help-text__failure--save',
        andHandleNow: true
      })(error);
    }
  }, [dispatch]);


  const onClickUpgradePathTrigger = useCallback((route) => {
    // react router can't handle full urls :P
    window.location = route;
  }, []);

  const onSelectCrop = useCallback(({ id }) => {
    try {
      // parsing this back to int here because crate forces your
      // option ids to be strings.
      const cropId = parseInt(id, 10);
      dispatch(setActiveCropId(cropId));
      dispatch(fetchBrandsByActiveCrop());
    } catch (error) {
      treatErrorAs({
        severity: errorLevel.WARN,
        messageKey: 'help-text__failure--save',
        andHandleNow: true
      })(error);
    }
  }, [dispatch]);

  const saveCropPlan = useCallback((cropPlanToSave) => {
    const cropPlanLegacyCropId = crops[cropPlanToSave.cropId]['legacy-id'];
    
    dispatch(setActiveScriptType(cropPlanToSave.scriptType));
    dispatch(setActiveCropId(cropPlanLegacyCropId));
    dispatch(setActiveCropPlan(cropPlanToSave));
  }, [crops, dispatch]);

  useEffect(() => {
    dispatch(requestActivities());
    dispatch(requestAdvancedScriptAvailability());
  }, [dispatch]);

  useEffect(() => {
    if (cropPlanParam) {
      setCropPlan(mockedCropPlan);
      saveCropPlan(mockedCropPlan);
    }
    if (isCropPlanIntegration && cropPlan) {
      saveCropPlan(cropPlan);
    }
  }, [cropPlan, cropPlanParam, isCropPlanIntegration, saveCropPlan, setCropPlan]);

  const advancedScriptClickHandler = useMemo(() => canCreateAdvancedScript
    ? () => onClickAdvancedScriptTrigger()
    : () => onClickUpgradePathTrigger(upgradePath),
  [canCreateAdvancedScript, onClickAdvancedScriptTrigger, onClickUpgradePathTrigger, upgradePath]);

  const manualScriptClickHandler = useMemo(() => canCreateManualScript
    ? () => onClickManualScriptTrigger()
    : () => onClickUpgradePathTrigger(upgradePath),
  [canCreateManualScript, onClickManualScriptTrigger, onClickUpgradePathTrigger, upgradePath]);

  const scriptOptions = useMemo(() => {
    switch (activeScriptType) {
      case scriptTypes.FERTILITY:
        return (<FertilityScriptOptions
          canCreateManualScript={canCreateManualScript}
          isApplication={false}
          onClickManualScriptTrigger={manualScriptClickHandler}
        />);
      case scriptTypes.APPLICATION:
        return (<FertilityScriptOptions
          canCreateManualScript={canCreateManualScript}
          isApplication={true}
          onClickManualScriptTrigger={manualScriptClickHandler}
        />);
      case scriptTypes.PLANTING:
        return (<PlantingScriptOptionsConnected
          activeCropId={activeCropId}
          canCreateAdvancedScript={canCreateAdvancedScript}
          canCreateManualScript={canCreateManualScript}
          currentCropCanAdvancedScript={cropCanAdvancedScript}
          onClickAdvancedScriptTrigger={advancedScriptClickHandler}
          onClickManualScriptTrigger={manualScriptClickHandler}
          onSelectCrop={onSelectCrop}
          supportedCrops={supportedCrops}
        />);
      default:
        return null;
    }
  }, [
    activeScriptType,
    canCreateManualScript,
    manualScriptClickHandler,
    activeCropId,
    canCreateAdvancedScript,
    cropCanAdvancedScript,
    advancedScriptClickHandler,
    onSelectCrop,
    supportedCrops
  ]);

  return (
    <section
      className={screenClassString}
      data-test-id='vra-script-type'>
      <ScriptTypeToggleContainer />
      {scriptOptions}
    </section>
  );
};

export default ScriptOptions;
